import React, { useEffect, useState } from 'react'
import { Alert, Button, Card, Modal, Radio, Space, Table, Tag, Tooltip } from 'antd'
import PageLoader from '../../components/Layouts/PageLoader'
import {
  deleteRequest,
  errorNotification,
  getRequest,
  patchRequest,
  successNotification,
  trackingLineUrl,
  trackingLinesUrl
} from '../../config'
import LineFormModal from './LineFormModal'
import {
  CODE_SENTENCES,
  KIND_LABELS,
  errorSentence,
  formatDate,
  lineOffReason,
  lineProblems,
  lineSource,
  shipsgoNameDiffers
} from './utils'

// The columns are a factory so the row buttons can close over this page's handlers. antd `render`
// functions return JSX, which the display-name rule reads as an anonymous component; they are cell
// renderers, disabled here the same way the rest of the dashboard does it.
/* eslint-disable react/display-name */
const lineColumns = ({ onEdit, onDelete, onDeactivate }) => [
  { title: 'Position', key: 'position', render: (_, r) => r.position },
  {
    title: 'Name',
    key: 'name',
    render: (_, r) => (
      <span>
        <div>{r.name}</div>
        {/* ShipsGo's own name beneath ours when they differ, so a display rule that got a
            catalogue row wrong is visible here rather than only to a customer. */}
        {shipsgoNameDiffers(r) ? (
          <div style={{ opacity: 0.6, fontSize: 12 }}>ShipsGo: {r.shipsgoName}</div>
        ) : null}
      </span>
    )
  },
  {
    title: 'Source',
    key: 'source',
    render: (_, r) =>
      lineSource(r) === 'curated' ? (
        <Tooltip title="Set up by a person: prefixes, RoRo and order are ours and the sync never changes them.">
          <Tag color="blue">Curated</Tag>
        </Tooltip>
      ) : (
        <Tooltip title="Imported from ShipsGo's carrier list by the nightly sync. Editing it makes it curated.">
          <Tag>Catalogue</Tag>
        </Tooltip>
      )
  },
  { title: 'SCAC', key: 'scac', render: (_, r) => r.scac || '—' },
  {
    title: 'ShipsGo code',
    key: 'shipsgoCarrierCode',
    render: (_, r) => (r.shipsgoCarrierCode ? <code>{r.shipsgoCarrierCode}</code> : '—')
  },
  {
    title: 'Prefixes',
    key: 'prefixes',
    render: (_, r) =>
      (r.prefixes || []).length
        ? (r.prefixes || []).map(prefix => (
            <Tag key={prefix} style={{ marginBottom: 2 }}>
              {prefix}
            </Tag>
          ))
        : '—'
  },
  {
    title: 'Tracks',
    key: 'tracks',
    render: (_, r) => (
      <span>
        {r.tracksContainer ? <Tag>{KIND_LABELS.container}</Tag> : null}
        {r.tracksReference ? <Tag>{KIND_LABELS.reference}</Tag> : null}
        {!r.tracksContainer && !r.tracksReference ? '—' : null}
      </span>
    )
  },
  {
    title: 'RoRo',
    key: 'roro',
    render: (_, r) => (r.roro ? <Tag color="purple">RoRo</Tag> : '—')
  },
  {
    title: 'Active',
    key: 'active',
    render: (_, r) => {
      if (r.active) return <Tag color="green">Yes</Tag>
      // Who switched it off matters: the sync's off reverses itself when the code verifies
      // again; a person's does not (spec: tracking-carrier-catalogue D3).
      const reason = lineOffReason(r)
      return (
        <span>
          <Tag>No</Tag>
          {reason ? <div style={{ opacity: 0.6, fontSize: 12 }}>{reason.label}</div> : null}
        </span>
      )
    }
  },
  {
    title: 'Reference prefixes',
    key: 'referencePrefixes',
    render: (_, r) =>
      (r.referencePrefixes || []).length
        ? (r.referencePrefixes || []).map(prefix => (
            <Tag key={prefix} style={{ marginBottom: 2 }}>
              {prefix}
            </Tag>
          ))
        : '—'
  },
  {
    title: 'In use',
    key: 'inUse',
    render: (_, r) =>
      `${r.inUse || 0} ${Number(r.inUse) === 1 ? 'shipment' : 'shipments'}`
  },
  {
    title: 'Verified',
    key: 'verified',
    render: (_, r) =>
      r.verifiedAgainstShipsgoAt ? (
        formatDate(r.verifiedAgainstShipsgoAt)
      ) : (
        <Tooltip title="Set when sync_shipsgo_carriers --apply confirms the code against ShipsGo">
          <Tag color="gold">Not verified</Tag>
        </Tooltip>
      )
  },
  {
    title: 'Problems',
    key: 'problems',
    render: (_, r) => {
      const problems = lineProblems(r)
      if (!problems.length) return '—'
      return problems.map(problem => (
        <Tag color="red" key={problem} style={{ marginBottom: 2 }}>
          {problem}
        </Tag>
      ))
    }
  },
  {
    title: '',
    key: 'actions',
    render: (_, r) => (
      <Space>
        <Button size="small" onClick={() => onEdit(r)}>
          Edit
        </Button>
        {/* Shipments on a line hold it with a PROTECT foreign key, so the server would refuse the
            delete anyway. Offering the thing that does work is kinder than offering the refusal. */}
        {r.inUse > 0 ? (
          <Button size="small" onClick={() => onDeactivate(r)}>
            Deactivate
          </Button>
        ) : (
          <Button size="small" danger onClick={() => onDelete(r)}>
            Delete
          </Button>
        )}
      </Space>
    )
  }
]
/* eslint-enable react/display-name */

// The list customers pick from. A code that ShipsGo does not recognise does not fail here — it
// fails when the shipment is created, after the customer has paid — so this page says so out loud
// and flags every line that has never been verified against ShipsGo's own carrier list.
const LinesPage = () => {
  const [loading, setLoading] = useState(false)
  const [lines, setLines] = useState([])
  const [editing, setEditing] = useState(null) // { line } — a null line is "add"
  const [source, setSource] = useState('all') // all | curated | catalogue (client-side)

  const load = async () => {
    const response = await getRequest(trackingLinesUrl, 'shipping lines', setLoading)
    if (response.success) {
      const rows = response.data.results || response.data || []
      // Position order, the app's own order; name breaks a tie so the table never reshuffles
      // itself between reloads.
      setLines(
        [...rows].sort(
          (a, b) =>
            (a.position || 0) - (b.position || 0) ||
            String(a.name || '').localeCompare(String(b.name || ''))
        )
      )
      return
    }
    errorNotification(errorSentence(response, 'The shipping lines could not be loaded.'))
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line
  }, [])

  const deactivate = async row => {
    const response = await patchRequest(
      trackingLineUrl(row.id),
      { active: false },
      'line',
      () => {}
    )
    if (!response.success) {
      errorNotification(errorSentence(response, 'That line could not be switched off.'))
      return
    }
    successNotification('Line switched off.')
    await load()
  }

  const confirmDeactivate = row =>
    Modal.confirm({
      title: `Switch ${row.name} off?`,
      content:
        'Customers stop seeing it; shipments on it are not affected. While shipments use it, it cannot be deleted.',
      okText: 'Deactivate',
      onOk: () => deactivate(row)
    })

  const remove = async row => {
    const response = await deleteRequest(trackingLineUrl(row.id), 'line', () => {})
    if (response.success) {
      successNotification('Line deleted.')
      await load()
      return
    }
    // The safety net for a row whose `inUse` was stale — a request may have been created on this
    // line since the table was loaded.
    if (response.error?.response?.data?.code === 'line_in_use') {
      Modal.confirm({
        title: 'This line is in use',
        content: `${errorSentence(
          response,
          CODE_SENTENCES.line_in_use
        )} Deactivate it instead? Customers stop seeing it; shipments on it are not affected.`,
        okText: 'Deactivate',
        onOk: () => deactivate(row)
      })
      return
    }
    errorNotification(errorSentence(response, 'That line could not be deleted.'))
  }

  const confirmDelete = row =>
    Modal.confirm({
      title: `Delete ${row.name}?`,
      content:
        'Customers will no longer see it. Shipments already on it keep it. If it is in use the server will refuse — switch it off instead.',
      okType: 'danger',
      onOk: () => remove(row)
    })

  return (
    <PageLoader className="container" loading={loading}>
      <Card
        title="Shipping lines"
        extra={
          <Button type="primary" onClick={() => setEditing({ line: null })}>
            Add a line
          </Button>
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="The ShipsGo carrier code must match ShipsGo's carrier list exactly. A wrong code creates every shipment on this line against the wrong carrier and spends a credit for nothing."
          description="The list is every carrier ShipsGo can track: the nightly sync imports its carrier list as Catalogue rows and verifies the Curated ones (the rows a person set up — prefixes, RoRo, order). The customer sees curated lines first, in position order, then the rest by name."
        />

        <Radio.Group
          value={source}
          onChange={e => setSource(e.target.value)}
          style={{ marginBottom: 12 }}
          optionType="button"
          options={[
            { label: 'All', value: 'all' },
            { label: 'Curated', value: 'curated' },
            { label: 'Catalogue', value: 'catalogue' }
          ]}
        />

        <Table
          rowKey="id"
          dataSource={source === 'all' ? lines : lines.filter(row => lineSource(row) === source)}
          columns={lineColumns({
            onEdit: row => setEditing({ line: row }),
            onDelete: confirmDelete,
            onDeactivate: confirmDeactivate
          })}
          pagination={false}
          scroll={{ x: 1600 }}
          size="middle"
        />
      </Card>

      <LineFormModal
        visible={!!editing}
        line={editing ? editing.line : null}
        onClose={() => setEditing(null)}
        onDone={() => {
          setEditing(null)
          load()
        }}
      />
    </PageLoader>
  )
}

export default LinesPage
